import React, { useCallback, useEffect, useState } from "react";
import { CalendarClock, Trash2, Plus, Lock, Pencil, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const WD = [
  { v: 1, l: "Segunda" },
  { v: 2, l: "Terça" },
  { v: 3, l: "Quarta" },
  { v: 4, l: "Quinta" },
  { v: 5, l: "Sexta" },
  { v: 6, l: "Sábado" },
  { v: 0, l: "Domingo" },
];
const wdName = (v) => (WD.find((w) => w.v === v) || {}).l || "";
const CHURRAS = [
  { h: 7, l: "Manhã (07-12)" },
  { h: 12, l: "Tarde (12-18)" },
  { h: 18, l: "Noite (18-22)" },
];
const hoursFor = (type) =>
  type === "churrasqueira"
    ? CHURRAS
    : Array.from({ length: 17 }, (_, i) => ({ h: i + 7, l: `${String(i + 7).padStart(2, "0")}:00` }));
const hourName = (space, h) => {
  if (space?.type === "churrasqueira") {
    const c = CHURRAS.find((x) => x.h === h);
    return c ? c.l : `${h}:00`;
  }
  return `${String(h).padStart(2, "0")}:00`;
};

function BlockEdit({ b, spaces, onSaved, onCancel }) {
  const [f, setF] = useState({ space_id: b.space_id, weekday: b.weekday, hour: b.hour, label: b.label || "" });
  const sp = spaces.find((s) => s.id === f.space_id);
  const cls = "w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm";
  const save = async () => {
    try {
      await api.patch(`/admin/blocks/${b.id}`, { ...f, hour: Number(f.hour), label: f.label.trim() });
      toast.success("Horário fixo atualizado!");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <div data-testid={`block-edit-${b.id}`} className="grid grid-cols-1 gap-2 rounded-xl border border-[#1B7A45]/40 bg-emerald-50/40 p-3 sm:grid-cols-[1fr_1fr_1fr_1.5fr_auto]">
      <select data-testid={`block-edit-space-${b.id}`} className={cls} value={f.space_id} onChange={(e) => setF({ ...f, space_id: e.target.value })}>
        {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <select data-testid={`block-edit-weekday-${b.id}`} className={cls} value={f.weekday} onChange={(e) => setF({ ...f, weekday: Number(e.target.value) })}>
        {WD.map((w) => <option key={w.v} value={w.v}>{w.l}</option>)}
      </select>
      <select data-testid={`block-edit-hour-${b.id}`} className={cls} value={f.hour} onChange={(e) => setF({ ...f, hour: Number(e.target.value) })}>
        {hoursFor(sp?.type).map((o) => <option key={o.h} value={o.h}>{o.l}</option>)}
      </select>
      <Input data-testid={`block-edit-label-${b.id}`} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Motivo / mensalista" className="rounded-xl bg-white" />
      <div className="flex gap-1.5">
        <Button data-testid={`block-edit-save-${b.id}`} onClick={save} size="sm" className="rounded-full bg-[#1B7A45] text-white hover:bg-[#156337]"><Save className="h-4 w-4" /></Button>
        <Button data-testid={`block-edit-cancel-${b.id}`} onClick={onCancel} size="sm" variant="outline" className="rounded-full"><X className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export default function BlocksManager() {
  const [spaces, setSpaces] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [spaceId, setSpaceId] = useState("");
  const [weekday, setWeekday] = useState(1);
  const [hour, setHour] = useState(19);
  const [label, setLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sp, bl] = await Promise.all([api.get("/spaces"), api.get("/admin/blocks")]);
      setSpaces(sp.data);
      setBlocks(bl.data);
      setSpaceId((prev) => prev || (sp.data[0]?.id ?? ""));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const currentSpace = spaces.find((s) => s.id === spaceId);
  const hourOptions = hoursFor(currentSpace?.type);

  useEffect(() => {
    if (!currentSpace) return;
    if (currentSpace.type === "churrasqueira" && ![7, 12, 18].includes(hour)) setHour(7);
    if (currentSpace.type !== "churrasqueira" && (hour < 7 || hour > 23)) setHour(19);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId]);

  const add = async () => {
    try {
      await api.post("/admin/blocks", {
        space_id: spaceId,
        weekday,
        hour: Number(hour),
        label: label.trim(),
      });
      toast.success("Horário fixo adicionado!");
      setLabel("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/blocks/${id}`);
      toast.success("Horário fixo removido.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const selCls = "rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800";

  return (
    <div className="mt-8" data-testid="blocks-manager">
      <div className="flex items-center gap-2">
        <Lock className="h-5 w-5 text-[#1B7A45]" />
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">
          Horários fixos (bloqueios)
        </h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Bloqueie um horário recorrente toda semana (ex.: aula com professor). O horário fica
        indisponível para reservas dos clientes.
      </p>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <select
            data-testid="block-space"
            className={selCls}
            value={spaceId}
            onChange={(e) => setSpaceId(e.target.value)}
          >
            {spaces.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <select
            data-testid="block-weekday"
            className={selCls}
            value={weekday}
            onChange={(e) => setWeekday(Number(e.target.value))}
          >
            {WD.map((w) => (
              <option key={w.v} value={w.v}>
                {w.l}
              </option>
            ))}
          </select>
          <select
            data-testid="block-hour"
            className={selCls}
            value={hour}
            onChange={(e) => setHour(Number(e.target.value))}
          >
            {hourOptions.map((o) => (
              <option key={o.h} value={o.h}>
                {o.l}
              </option>
            ))}
          </select>
          <Input
            data-testid="block-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Motivo (ex.: Professor João)"
            className="rounded-xl border-slate-200"
          />
        </div>
        <Button
          data-testid="block-add-btn"
          onClick={add}
          className="mt-3 rounded-xl bg-[#1B7A45] px-6 py-5 font-semibold text-white hover:bg-[#156337]"
        >
          <Plus className="mr-2 h-4 w-4" /> Adicionar horário fixo
        </Button>

        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="h-16 animate-pulse rounded-xl bg-slate-100" />
          ) : blocks.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-400">
              Nenhum horário fixo cadastrado.
            </p>
          ) : (
            blocks.map((b) => {
              const sp = spaces.find((s) => s.id === b.space_id);
              if (editId === b.id) return <BlockEdit key={b.id} b={b} spaces={spaces} onCancel={() => setEditId(null)} onSaved={() => { setEditId(null); load(); }} />;
              return (
                <div
                  key={b.id}
                  data-testid={`block-item-${b.id}`}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 p-3"
                >
                  <CalendarClock className="h-5 w-5 shrink-0 text-[#1B7A45]" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800">
                      {sp ? sp.name : b.space_id} · {wdName(b.weekday)} · {hourName(sp, b.hour)}
                    </p>
                    {b.label && <p className="text-xs text-slate-500">{b.label}</p>}
                  </div>
                  <Button
                    data-testid={`block-editbtn-${b.id}`}
                    onClick={() => setEditId(b.id)}
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    data-testid={`block-delete-${b.id}`}
                    onClick={() => remove(b.id)}
                    size="sm"
                    variant="outline"
                    className="rounded-full border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
